// @flow
import React from 'react';
import { connect } from 'react-redux';
import { Grid, Row, Col } from 'react-flexbox-grid';
import {
  MdClose,
  MdSearch,
} from 'react-icons/lib/md';
import {
  reduxForm,
  Field,
  formValueSelector,
} from 'redux-form';
import i18n from 'i18n-js';

import * as selectors from '../../../reducers/index';
import {
  GOING_ROUTE_TYPE,
  RETURNING_ROUTE_TYPE,
} from '../../../types/routes';
import type { GATEWAY_TYPE } from '../../../types/gateways';
import { OMNISEARCH_RESULT_TYPES } from '../../Omnisearch/index';
import {
  onSubmit,
  validate,
} from './utils';
import PopupHeader from '../../PopupHeader/index';
import PopupHeaderRightGroup from '../../PopupHeaderRightGroup/index';
import PopupHeaderLeftGroup from '../../PopupHeaderLeftGroup/index';
import PopupContent from '../../PopupContent/index';
import PopupFooter from '../../PopupFooter/index';
import PopupHeaderTitle from '../../PopupHeaderTitle/index';
import Button from '../../Button/index';
import styles from './createChangeRequestPopupForm.css';
import PopupCloseButton from '../../PopupCloseButton/index';
import {
  RenderTextarea,
  RenderSelect,
  RenderDateRangePicker,
  RenderTypeahead,
} from '../../FieldRenderer/index';
import OmnisearchLoadingResults from '../../OmnisearchLoadingResults/index';
import OmnisearchResult from '../../OmnisearchResult/index';
import { generateFetcher } from '../../Omnisearch/utils';
import CloseableInputSearchResult from '../../CloseableInputSearchResult/index';
import type { ERROR_TYPE } from '../../../types/common';
import {
  Cancel, CreateButton, Search,
} from '../../CommonTexts';
import { getUserImage } from '../../../utils';


type CreateRoutePopupFormPropTypes = {
  handleSubmit: Function,
  errorForm: ERROR_TYPE,
  fetchRouteStops: string => Promise<Array<Object>>,
  fetchStudents: string => Promise<Array<Object>>,
  gatewayOptions: string => Array<Object>,
  selectedToGateway: GATEWAY_TYPE,
};

const customRenderSelected = (item, onClear) => {
  switch (item.result_type) {
    case OMNISEARCH_RESULT_TYPES.student: {
      return (
        <CloseableInputSearchResult
          title={`${item.first_name} ${item.last_name}`}
          subtitle={i18n.t('labels.students', { count: 1 })}
          closeAction={onClear}
          pictureURL={getUserImage(item.profile_picture)}
        />
      );
    }

    case OMNISEARCH_RESULT_TYPES.routeStop: {
      return (
        <CloseableInputSearchResult
          title={`${item.name} - ${item.short_name}`}
          subtitle={i18n.t('labels.stops')}
          closeAction={onClear}
        />
      );
    }
    default: return null;
  }
};

const customRenderItem = (item: Object, isHighlighted: boolean) => (
  <OmnisearchResult
    {...item}
    isSelected={isHighlighted}
    size="small"
  />
);

const CreateChangeRequestPopupForm = ({
  handleSubmit,
  gatewayOptions,
  errorForm,
  fetchRouteStops,
  selectedToGateway,
  fetchStudents,
}: CreateRoutePopupFormPropTypes) => {
  const CHANGE_TYPE_OPTIONS = [
    { value: GOING_ROUTE_TYPE, label: i18n.t('changeStopRequests.entry') },
    { value: RETURNING_ROUTE_TYPE, label: i18n.t('changeStopRequests.exit') },
  ];
  return (
    <form id="createChangeStopRequest" onSubmit={handleSubmit}>
      <PopupHeader>
        <PopupHeaderLeftGroup>
          <PopupHeaderTitle title={i18n.t('buttons.requestChange')} size="big" />
        </PopupHeaderLeftGroup>
        <PopupHeaderRightGroup>
          <PopupCloseButton
            size="medium"
            Icon={MdClose}
            tint="mediumGray"
            clearMargins
            isTransparent
            isOutlined
          />
        </PopupHeaderRightGroup>
      </PopupHeader>
      <PopupContent className={styles.createRoutePopupContent} size="large">
        <Grid>
          <Row>
            <Col className={styles.formErrors} xs={12}>
              <p>
                { errorForm
                  && errorForm.data
                  && errorForm.data.error
                  && 'Error al crear la solicitud: ' }
                { errorForm
                  && errorForm.data
                  && errorForm.data.error
                  && errorForm.data.error.detail_ES }
              </p>
            </Col>
          </Row>
          <Row>
            <Col xs={8}>
              <Field
                name="student"
                label={i18n.t('labels.students', { count: 1 })}
                component={RenderTypeahead}
                inputDebounce={200}
                renderSelectedValue={customRenderSelected}
                renderLoading={() => <OmnisearchLoadingResults size="small" count={3} />}
                fetchItems={fetchStudents}
                renderItem={customRenderItem}
                inputProps={{
                  Icon: MdSearch,
                  iconPosition: 'right',
                  placeholder: Search(i18n.t('labels.students', { count: 1 }), 1),
                }}
              />
            </Col>
            <Col xs={4}>
              <Field
                name="route_type"
                label={i18n.t('changeStopRequests.requestType')}
                component={RenderSelect}
                options={CHANGE_TYPE_OPTIONS}
              />
              <br />
            </Col>
          </Row>
          <Row>
            <Col className={styles.spacing} xs={12}>
              <Field
                name="date_range"
                required
                label={i18n.t('changeStopRequests.dateRange')}
                component={RenderDateRangePicker}
                renderItem={customRenderItem}
                dateChangeCallback={() => {
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <Field
                name="to_gateway"
                label={i18n.t('changeStopRequests.toStop')}
                component={RenderSelect}
                options={gatewayOptions}
              />
              <br />
            </Col>
          </Row>
          {
              selectedToGateway && selectedToGateway.name === 'Bus'
              && (
                <Row>
                  <Col className={styles.spacing} xs={12}>
                    <Field
                      name="to_stop"
                      label="El estudiante deberá ser recogido en:"
                      component={RenderTypeahead}
                      inputDebounce={200}
                      renderSelectedValue={customRenderSelected}
                      renderLoading={() => <OmnisearchLoadingResults size="small" count={3} />}
                      fetchItems={fetchRouteStops}
                      renderItem={customRenderItem}
                      inputProps={{
                        Icon: MdSearch,
                        iconPosition: 'right',
                        placeholder: Search(i18n.t('labels.stops'), 2),
                      }}
                    />
                  </Col>
                </Row>
              )
            }
          <Row>
            <Col xs={12}>
              <Field
                name="description"
                label={i18n.t('changeStopRequests.commentary')}
                component={RenderTextarea}
              />
            </Col>
          </Row>
        </Grid>
      </PopupContent>
      <PopupFooter size="large">
        <PopupCloseButton title={Cancel()} tint="paleGray" />
        <Button type="submit" title={CreateButton(i18n.t('common.changeStopRequest', { count: 1 }))} />
      </PopupFooter>
    </form>
  );
};


export default connect(
  (state, { gateways }) => {
    const schoolId = (selectors.getProfileOfType(state, 'admin') || {}).school || -1;
    const token = selectors.getToken(state);
    const error = selectors.getChangeStopRequestFetchingError(state);
    const fvs = formValueSelector('createChangeStopRequest');
    const selectedToGatewayId = fvs(state, 'to_gateway');
    const selectedToGateway = selectors.getGateway(state, selectedToGatewayId);
    const gatewayOptions = gateways.map(elm => ({
      value: elm.id, label: elm.name,
    }));
    const fetchMonitors = generateFetcher(
      OMNISEARCH_RESULT_TYPES.monitor,
      schoolId,
      token,
    );
    const fetchDrivers = generateFetcher(
      OMNISEARCH_RESULT_TYPES.busDriver,
      schoolId,
      token,
    );
    const fetchBuses = generateFetcher(
      OMNISEARCH_RESULT_TYPES.bus,
      schoolId,
      token,
    );

    const fetchStudents = generateFetcher(
      OMNISEARCH_RESULT_TYPES.student,
      schoolId,
      token,
    );

    const fetchRouteStops = generateFetcher(
      OMNISEARCH_RESULT_TYPES.routeStop,
      schoolId,
      token,
    );
    return {
      initialValues: {
        route_type: GOING_ROUTE_TYPE,
      },
      edit: false,
      schoolId,
      selectedToGateway,
      errorForm: error,
      fetchDrivers,
      fetchMonitors,
      fetchRouteStops,
      gateways,
      gatewayOptions,
      fetchBuses,
      fetchStudents,
    };
  },
  undefined,
)(reduxForm({
  form: 'createChangeStopRequest',
  onSubmit,
  validate,
})(CreateChangeRequestPopupForm));



// WEBPACK FOOTER //
// ./src/scripts/components/CreateChangeStopRequestPopupContent/CreateChangeRequestPopupForm/index.jsx